/**
 * Canonical list of every admin-panel sidebar entry. Imported by both
 * `AdminLayout.js` (renders the sidebar) and `AdminSidebarEditor.js`
 * (lets admins rename / hide / reorder items). Keep this the single
 * source of truth for admin navigation.
 */
export const ADMIN_SIDEBAR_SECTIONS = [
  { key: 'manage', label: 'Upravljanje', items: [
    { to: '/admin', label: 'Nadzorna ploča', end: true },
    { to: '/admin/analytics-live', label: 'Analitika uživo' },
    { to: '/admin/ai-content', label: 'Obrada sadržaja uz AI' },
    { to: '/admin/homepage-settings', label: 'Postavke Hero sekcije' },
    { to: '/admin/landing', label: 'Odredišne stranice' },
    { to: '/admin/breaking-news', label: 'Vijesti u prekidu' },
    { to: '/admin/breaking-expiry', label: 'Istek breaking vijesti' },
    { to: '/admin/streams', label: 'Upravljanje streamovima' },
  ] },
  { key: 'content', label: 'Sadržaj', items: [
    { to: '/admin/articles', label: 'Članci' },
    { to: '/admin/feeds', label: 'RSS/Web feedovi (V2)' },
    { to: '/admin/ai-settings', label: 'AI postavke' },
    { to: '/admin/llm-usage', label: 'AI potrošnja & logovi' },
    { to: '/admin/ai-prompts', label: 'AI promptovi' },
    { to: '/admin/blog', label: 'Blog' },
    { to: '/admin/sponsored', label: 'Sponzorirano' },
    { to: '/admin/sponsored-orders', label: 'Sponzorirane narudžbe' },
    { to: '/admin/rss', label: 'RSS izvori' },
    { to: '/admin/rss-analyzer', label: 'RSS analiza & tuner' },
    { to: '/admin/comments', label: 'Komentari' },
    { to: '/admin/static-pages', label: 'Stranice' },
    { to: '/admin/imprint', label: 'Impresum — Tim' },
    { to: '/admin/imprint-widget', label: 'Impresum widget' },
    { to: '/admin/media', label: 'Mediji' },
    { to: '/admin/categories', label: 'Kategorije' },
    { to: '/admin/series', label: 'Serija članaka' },
    { to: '/admin/authors', label: 'Autori' },
    { to: '/admin/quizzes', label: 'Kvizovi' },
  ] },
  { key: 'editorial', label: 'Uredništvo', items: [
    { to: '/admin/workflow', label: 'Uređivački tijek rada' },
    { to: '/admin/review', label: 'Članci za pregled' },
    { to: '/admin/bulk-ops', label: 'Skupne operacije' },
    { to: '/admin/content-schedule', label: 'Raspored sadržaja' },
    { to: '/admin/content-calendar', label: 'Kalendar sadržaja' },
    { to: '/admin/duplicates', label: 'Detekcija dupliciranja' },
    { to: '/admin/sanitize', label: 'Sanitacija sadržaja' },
  ] },
  { key: 'users-subs', label: 'Korisnici i pretplate', items: [
    { to: '/admin/users', label: 'Korisnici' },
    { to: '/admin/subscriptions', label: 'Pretplate' },
    { to: '/admin/premium-promo', label: 'Premium promocija' },
    { to: '/admin/promo-codes', label: 'Promotivni kodovi' },
    { to: '/admin/vouchers', label: 'Bonovi / Popusti' },
  ] },
  { key: 'finance', label: 'Financije', items: [
    { to: '/admin/payments', label: 'Plaćanja' },
    { to: '/admin/invoices', label: 'Računi' },
    { to: '/admin/pricing', label: 'Cjenik' },
    { to: '/admin/micropayments', label: 'Mikroplaćanja' },
    { to: '/admin/tips', label: 'Savjeti / Donacije' },
    { to: '/admin/monetization', label: 'Monetizacija' },
  ] },
  { key: 'portals', label: 'Portali', items: [
    { to: '/admin/jobs', label: 'Oglasi za posao' },
    { to: '/admin/classifieds', label: 'Oglasi' },
    { to: '/admin/times-posao', label: 'Times Posao portal' },
    { to: '/admin/sponsored-market', label: 'Sponzorirano tržište' },
  ] },
  { key: 'analytics-ai', label: 'Analitika i AI', items: [
    { to: '/admin/analytics', label: 'Analitika' },
    { to: '/admin/advanced-analytics', label: 'Napredna analitika' },
    { to: '/admin/ab-testing', label: 'A/B testiranje' },
    { to: '/admin/heatmap', label: 'Toplinska mapa čitanja' },
    { to: '/admin/visitor-map', label: 'Karta posjetitelja' },
    { to: '/admin/ai-recommendations', label: 'Preporuke sadržaja' },
    { to: '/admin/performance-forecast', label: 'Prognoza izvedbe' },
    { to: '/admin/competitor-tracking', label: 'Praćenje konkurencije' },
    { to: '/admin/churn-prediction', label: 'Predviđanje odlazaka' },
  ] },
  { key: 'seo', label: 'SEO i distribucija', items: [
    { to: '/admin/seo-dashboard', label: 'SEO nadzorna ploča' },
    { to: '/admin/content-health', label: 'Zdravlje sadržaja' },
    { to: '/admin/amp-discover', label: 'AMP i Google Discover' },
    { to: '/admin/newsletter-digest', label: 'Sažetak newslettera' },
    { to: '/admin/weekly-reports', label: 'Tjedni izvještaji' },
    { to: '/admin/syndication', label: 'Syndication API' },
  ] },
  { key: 'marketing', label: 'Marketing', items: [
    { to: '/admin/newsletter', label: 'Newsletter' },
    { to: '/admin/email-campaigns', label: 'Email kampanje' },
    { to: '/admin/push', label: 'Push obavijesti' },
    { to: '/admin/social', label: 'Društvene mreže' },
    { to: '/admin/social-schedule', label: 'Raspored društvenih mreža' },
    { to: '/admin/ads', label: 'Upravljanje oglasima' },
    { to: '/admin/free-ads?tab=free', label: 'Besplatni oglasi' },
    { to: '/admin/purchased-ads?tab=paid', label: 'Kupljeni oglasi' },
    { to: '/admin/ad-discounts', label: 'Rabat za dugoročni zakup' },
    { to: '/admin/sponsored-settings', label: 'Sponzorirani članak (postavke)' },
    { to: '/admin/ad-reports', label: 'Izvješće o oglasima' },
    { to: '/admin/adsense', label: 'Postavke AdSensea' },
    { to: '/admin/advertise-analytics', label: 'Analitika oglašavanja' },
  ] },
  { key: 'system', label: 'Sustav', items: [
    { to: '/admin/translate-jobs', label: 'Prijevodi' },
    { to: '/admin/polls', label: 'Ankete' },
    { to: '/admin/chat-sessions', label: 'Sesije čavrljanja' },
    { to: '/admin/documentation', label: 'Dokumentacija' },
    { to: '/admin/import/wordpress', label: 'Uvoz' },
    { to: '/admin/content-reports', label: 'Izvještaji o sadržaju' },
    { to: '/admin/audit-log', label: 'Revizijski zapis' },
    { to: '/admin/observability', label: 'Zdravlje sustava (legacy)' },
    { to: '/admin/system-health', label: 'Zdravlje sustava uživo' },
    { to: '/admin/security', label: 'Sigurnost' },
    { to: '/admin/settings', label: 'Postavke' },
    { to: '/admin/branding', label: 'Rebrendiranje' },
    { to: '/admin/widgets', label: 'Widgeti / Katalozi' },
    { to: '/admin/times-brands', label: 'Times Brendovi' },
    { to: '/admin/county-map', label: 'Karta županija' },
    { to: '/admin/crypto-widget', label: 'Kripto widget' },
    { to: '/admin/crypto-tips', label: 'Kripto napojnice' },
    { to: '/admin/portal-manage', label: 'Upravljanje portalom' },
    { to: '/admin/doctor-revenue', label: 'Zarada Doktora' },
    { to: '/admin/photo-check', label: 'Provjera fotografija' },
    { to: '/admin/menu-manager', label: 'Upravitelj izbornika (javni)' },
    { to: '/admin/sidebar-editor', label: 'Uredi admin izbornik' },
    { to: '/admin/version-history', label: 'Povijest verzija' },
    { to: '/admin/citizen-content', label: 'Građanski sadržaj' },
    { to: '/admin/partners', label: 'Partnerski linkovi' },
    { to: '/admin/database', label: 'Baza podataka' },
    { to: '/admin/complaints', label: 'Tužibaba prijave' },
    { to: '/admin/offer-generator', label: 'Ponuda generator' },
    { to: '/admin/footer-partners', label: 'Footer partneri' },
    { to: '/admin/polls-analytics', label: 'Ankete / Polls' },
  ] },
  { key: 'legacy', label: 'Ostalo', items: [
    { to: '/admin/news-submissions', label: 'Predane vijesti' },
    { to: '/admin/ad-inquiries', label: 'Upiti za oglase' },
    { to: '/admin/contact-messages', label: 'Kontakt poruke' },
    { to: '/admin/bug-reports', label: 'Prijavljeni bugovi' },
    { to: '/admin/error-log', label: 'Dnevnik grešaka' },
    { to: '/admin/email-log', label: 'Email zapis' },
    { to: '/admin/translations/missing', label: 'Neprevedeni sadržaj' },
    { to: '/admin/i18n-coverage', label: 'Pokrivenost prijevoda' },
    { to: '/admin/translation-provider', label: 'Prijevodi — provider' },
    { to: '/admin/tags', label: 'Oznake' },
  ] },
];
